import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@core/base.component';
import { ICON } from '@core/util/icon';
import { getMortgagePrimaryLender } from '@core/util/mortgage';
import { base64ToBlob } from '@core/util/string';
import { MortgageSourceTag, MortgageTag } from '@core/util/tags';
import { base64ToString, isSet } from '@core/util/value';
import {
	FieldMapping,
	FormInstance,
	FormTemplateWithVersion,
	FormTypes,
	GlobalMortgageFormTemplateLabels,
} from '@models/global-forms';
import { MatterBusinessUser } from '@models/matter';
import {
	MatterDocumentType,
	MortgageDocumentSource,
} from '@models/matter-file';
import { MatterFormsV2 } from '@models/matter-forms';
import {
	MatterMortgageType,
	MatterMortgageWithAssociations,
	SYNC_FLOW_TYPES,
} from '@models/mortgage';
import { MergedFileWithContextData } from '@models/mortgage-document-packages';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { GlobalFormsVersionsRepo } from '@repos/global-forms-versions.ts/global-forms-verisons.repo';
import { GlobalFormsRepo } from '@repos/global-forms/global-forms.repo';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { FileDownloadDispatch } from '@state/file-download/fileDownload.index';
import {
	FileDownload,
	FileDownloadType,
} from '@state/file-download/fileDownload.model';
import { CustomTags } from '@state/global/tags/tags.models';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { MatterFormV2DataState } from '@state/matter/form-data-v2/form-data-v2.state';
import { MatterMortgagePackagesState } from '@state/matter/matter-mortgage-packages';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { isBefore, parse, startOfDay } from 'date-fns';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';
import {
	BehaviorSubject,
	Observable,
	combineLatest,
	firstValueFrom,
	lastValueFrom,
} from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import {
	UploadDocumentModalComponent,
	UploadModalDocumentData,
} from 'src/app/private/sale/documents/uploaded/modal/modal.component';
import {
	CloserSyncInstructionsAPIService,
	PreparePackageRequest,
} from 'src/app/shared/api-repos/mortgages/closer-sync-instructions.api.service';
import {
	PACKAGE_STATUS_TYPES,
	PackageStatus,
} from '../../../closer-sync-mortgage/closer-sync-mortgage.model';
import { CloserSyncMortgageSelectors } from '../../../closer-sync-mortgage/closer-sync-mortgage.selector';
import {
	CloserSyncMortgageDetailsSelectors,
	SelectedUploadedDoc,
} from './closer-sync-generate-package.selector';
import {
	LenderDocument,
	MortgageStepType,
	SendPackageToLenderComponent,
	SendPackageToLenderComponentDialogData,
} from './components/send-package-to-lender/send-package-to-lender.component';
import { MatterUploadedFilesDispatch } from '@state/matter/uploaded-files/matter-uploaded-files.dispatch';
import { GlobalFinancialInstitutionsState } from '@state/global-financial-institutions/global-financial-institutions.state';
import { FinancialInstitution } from '@models/lender';
import { BusinessUser } from '@models/businesses';
import { NavigationService } from '@services/nav/nav.service';
import { PILL_TYPES } from 'src/app/ui/pill/pill.component';
import { Logger } from '@core/logger';
import { GlobalFormsPdfService } from '@services/pdf/global-forms-pdf.service';

interface VM {
	forms: FormInstanceVM[];
	chargeTypes: string[];
	selectedFormPath: string;
	loadingForm: boolean;
	selectedForm: FormInstance;
	uploadedDocuments: SelectedUploadedDoc[];
	undeletableUserUploadedDocIds: string[];
	voidChequePath: string;
	showFormViewer: boolean;
	uploadedDocumentUrl: string;
	selectedCount: number;
	lenderName: string;
	lenderFormUrl: string;
	lenderFormLastCheckedAt: number;
	packageFiles: { [key: string]: MergedFileWithContextData };
	downloading: boolean;
	syncFlowType: SYNC_FLOW_TYPES;
	isLawyerConnected: boolean;
}

const defaultVM: VM = {
	forms: [],
	chargeTypes: [],
	selectedFormPath: null,
	loadingForm: true,
	selectedForm: null,
	uploadedDocuments: [],
	undeletableUserUploadedDocIds: [],
	voidChequePath: null,
	showFormViewer: false,
	uploadedDocumentUrl: null,
	selectedCount: 0,
	lenderName: '',
	lenderFormUrl: '',
	lenderFormLastCheckedAt: null,
	packageFiles: {},
	downloading: false,
	syncFlowType: null,
	isLawyerConnected: false,
};

interface FormInstanceVM extends FormInstance {
	selected: boolean;
	labels: GlobalMortgageFormTemplateLabels;
	formFieldMappings: FieldMapping[];
}

function needsCoversheetForm(
	_selectedForms: FormInstanceVM[],
	isDownload = false,
): boolean {
	if (isDownload) {
		Logger.info('Cover sheet is not required w/ download. Skipping!');
		return false;
	}

	// if none of the covered types have been selected, a cover should be added.
	const coveredTypes = [
		FormTypes['COVER_SHEET'],
		FormTypes['REQUEST_FOR_FUNDS'],
		FormTypes['FINAL_REPORT'],
	];
	return !_selectedForms.some(
		(x) =>
			coveredTypes.includes(x.labels.formType) ||
			x.name.toLocaleLowerCase().includes('cover'),
	);
}

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-generate-package',
	templateUrl: './closer-sync-generate-package.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncGeneratePackageComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy
{
	PACKAGE_STATUS_TYPES = PACKAGE_STATUS_TYPES;
	SYNC_FLOW_TYPES = SYNC_FLOW_TYPES;
	disableSendToLender = false;
	lenderForms: FormInstanceVM[];
	chargeTypes: string[];
	selectedChargeType: string;
	selectedLenderId: string;
	selectedDocuments: SelectedUploadedDoc[];
	_mortgage: MatterMortgageWithAssociations;
	formData: MatterFormsV2[];
	loadedMortgage = false;
	decryptionInProgress = false;
	tags: CustomTags;
	pageBuilderSubject: BehaviorSubject<FormTypes> = new BehaviorSubject(null);
	debounceTime = 0;
	ICON = ICON;
	PILL_TYPES = PILL_TYPES;
	matterMortgageType = MatterMortgageType;
	currentDate = new Date();

	@Select(MatterFormV2DataState.getAllFormData) formData$: Observable<
		MatterFormsV2[]
	>;
	@SelectSnapshot(SelectedMatter.assignedLawyer) lawyer: MatterBusinessUser;
	@Select(TagsSelector.getTags) tags$: Observable<CustomTags>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.closingDate) matterClosingDate: string;
	@Select(MatterMortgagesState.getSelectedMortgage)
	mortgage$: Observable<MatterMortgageWithAssociations>;
	@Select(CloserSyncMortgageDetailsSelectors.getUserUploadedDocs)
	uploadedDocs$: Observable<SelectedUploadedDoc[]>;
	@Select(CloserSyncMortgageDetailsSelectors.getUndeletableUserUploadedDocIds)
	undeletableUserUploadedDocIds$: Observable<string[]>;
	@Select(MatterMortgagePackagesState.getPackageFiles)
	mortgagePackages$: Observable<{
		[Globalskey: string]: MergedFileWithContextData;
	}>;
	@Select(CloserSyncMortgageSelectors.getRequestForFundsPackageStatus)
	rffPackageStatus$: Observable<PackageStatus>;
	@Select(GlobalFinancialInstitutionsState.getSyncInstitutions)
	syncInstitutions$: Observable<FinancialInstitution[]>;
	@Select(SelectedMatter.assignedLawyerUser)
	lawyer$: Observable<BusinessUser>;

	@Output() gotoHistory = new EventEmitter();
	@Input() syncFlowType: SYNC_FLOW_TYPES;

	constructor(
		private globalMortgageFormsRepo: GlobalFormsRepo,
		private globalFormsVersionRepo: GlobalFormsVersionsRepo,
		private dialog: MatDialog,
		private fileDownloadDispatch: FileDownloadDispatch,
		private globalFormsPdfService: GlobalFormsPdfService,
		private closerSyncInstructionsAPIService: CloserSyncInstructionsAPIService,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
		private matterUploadedFilesDispatch: MatterUploadedFilesDispatch,
		private navService: NavigationService,
	) {
		super(defaultVM);
		this.pageBuilderSubject
			.pipe(
				filter(() => !!this.tags && !!this._mortgage),
				debounceTime(this.debounceTime),
			)
			.subscribe((x: FormTypes) => {
				//sets the debounceTime after the first successful run - this speeds up first initial viewing
				this.debounceTime = 350;
				this.pageUpdated(x);
			});
	}

	ngOnDestroy() {
		this.pageBuilderSubject.unsubscribe();
	}

	async ngOnInit() {
		this.formData$.pipe(untilDestroyed(this)).subscribe((data) => {
			this.formData = data;
		});

		this.mortgagePackages$.pipe(untilDestroyed(this)).subscribe((files) => {
			this.VM = { packageFiles: files };
		});

		this.tags$.pipe(untilDestroyed(this)).subscribe((_tags) => {
			this.tags = _tags;
			this.pageBuilderSubject.next(null);
		});

		combineLatest([this.syncInstitutions$, this.mortgage$])
			.pipe(untilDestroyed(this))
			.subscribe(([syncInstitutions, mortgage]) => {
				if (this._mortgage?.id !== mortgage?.id) {
					this.VM = {
						selectedCount: 0,
						forms: [],
						selectedForm: null,
						selectedFormPath: null,
					};
				}
				if (!!mortgage && !this.loadedMortgage) {
					this.loadedMortgage = true;

					this._mortgage = mortgage;
					const lender = Object.values(mortgage?.lenders)[0];

					this.selectedChargeType = mortgage.chargeType?.toUpperCase();
					this.selectedLenderId = lender?.referenceId ?? lender?.id;

					this.pageBuilderSubject.next(null);

					const globalSyncInstitution = syncInstitutions?.find(
						(institution) => institution.closerSyncId === lender?.closerSyncId,
					);

					this.VM = {
						lenderName: globalSyncInstitution?.shortName,
						lenderFormLastCheckedAt: globalSyncInstitution?.lastFormChecked,
						syncFlowType: this.syncFlowType ?? mortgage?.syncFlowType,
					};
				}
				this.loadedMortgage = false;
			});

		this.rffPackageStatus$.pipe(untilDestroyed(this)).subscribe((rff) => {
			this.disableSendToLender =
				rff.status === this.PACKAGE_STATUS_TYPES.PREPARE;
		});

		this.uploadedDocs$.pipe(untilDestroyed(this)).subscribe((docs) => {
			const uploadedDocuments = docs.map((doc) => {
				const prevDoc = this.VM.uploadedDocuments.find(
					(prevDoc) => prevDoc.id === doc.id,
				);
				doc.selected = prevDoc?.selected ?? false;
				return cloneDeep(doc);
			});

			this.VM = { uploadedDocuments };
		});

		this.undeletableUserUploadedDocIds$
			.pipe(untilDestroyed(this))
			.subscribe((ids) => {
				this.VM = { undeletableUserUploadedDocIds: ids };
			});

		this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
			const selectedFormType = params['selectedForm'];

			if (selectedFormType) {
				this.pageBuilderSubject.next(selectedFormType);
			}
		});

		this.lawyer$.pipe(untilDestroyed(this)).subscribe((lawyer) => {
			this.VM = {
				isLawyerConnected: !!lawyer.closerSyncId && !!lawyer.signature,
			};
		});
	}

	async pageUpdated(selectedFormType?: FormTypes) {
		if (this._mortgage && this.tags) {
			try {
				const forms = await this.loadForms();
				this.VM = { forms };

				if (!forms?.length) {
					this.VM = { loadingForm: false };
					return;
				}

				let selected = this.VM.selectedForm?.context?.id;

				if (selectedFormType) {
					const form = forms.find(
						(form) => form.labels.formType === selectedFormType,
					);

					selected = form?.context?.id;
				}

				if (!selected && forms?.length > 0) {
					selected = forms[0].context.id;
				}

				this.VM = { loadingForm: false };
				this.selectForm(selected);
			} catch (error) {
				LogRocket.captureException(error);
				console.error('Error while loading forms:', error);
			}
		}
	}

	calculateSelectedTotal() {
		const totalFormsSelected =
			this.VM.forms.filter((form) => form.selected).length ?? 0;
		const totalDocsSelected =
			this.VM.uploadedDocuments.filter((doc) => doc.selected).length ?? 0;

		this.VM = { selectedCount: totalFormsSelected + totalDocsSelected };
	}

	async selectForm(formId: string) {
		const formInstance = cloneDeep(
			this.VM.forms?.find((form) => form.context?.id === formId),
		);

		if (!formInstance) {
			LogRocket.error(`Form with id not found`, formId);
			console.error(`Form with id ${formId} not found`);
			return;
		}

		const fields = await this.globalFormsPdfService.evaluateFields(
			formInstance?.formFieldMappings,
			formInstance?.context,
		);

		formInstance.fields = fields || {};

		this.VM = {
			selectedForm: formInstance,
			showFormViewer: true,
			uploadedDocumentUrl: null,
		};
	}

	selectUploadedDocument(doc: SelectedUploadedDoc) {
		this.VM = {
			uploadedDocumentUrl: doc.url,
			showFormViewer: false,
			selectedForm: null,
		};
	}

	uploadDocument(): void {
		this.analytics.logEvent(AnalyticsEvent.SYNC_MORTGAGE_PAGE_UPLOAD);
		const data: UploadModalDocumentData = {
			matterId: this.matterId,
			defaultFileType: MatterDocumentType.MORTGAGE,
			lockFileType: true,
			systemTags: [
				MortgageTag(this._mortgage?.id),
				MortgageSourceTag(MortgageDocumentSource.USER_UPLOAD),
			],
			contextId: this._mortgage?.id,
			hideSubTypes: true,
		};
		this.dialog.open(UploadDocumentModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	async loadForms() {
		// here
		// Query the database for all forms that match the selected lender and charge type
		if (!this.selectedLenderId || !this.selectedChargeType) {
			return;
		}

		const formTemplates =
			await this.globalMortgageFormsRepo.getMortgageFormsByLenderChargeType(
				this.selectedLenderId,
				this.selectedChargeType,
			);

		if (!formTemplates.length) return [];
		// Get the current version of each form
		const formTemplateVersions = await Promise.all(
			formTemplates.map(async (form) => {
				const formVersion = await firstValueFrom(
					this.globalFormsVersionRepo.Get$(form.currentVersion, {
						globalFormId: form.id,
					}),
				);
				return {
					...form,
					version: formVersion || {},
				} as FormTemplateWithVersion;
			}),
		);

		const formsBuilt = await this.generateFormInstances(
			formTemplateVersions,
			this.tags,
		);

		const forms = formsBuilt?.filter(
			(x) =>
				(x?.context?.mortgage as MatterMortgageWithAssociations)?.id ===
				this._mortgage.id,
		);
		const sortedForms = forms.sort((a, b) => a.name.localeCompare(b.name));

		const coversheets = sortedForms.filter(
			(form) => form.labels.formType === FormTypes.COVER_SHEET,
		);
		const main = sortedForms
			.filter(
				(form) =>
					form.labels.formType === FormTypes.REQUEST_FOR_FUNDS ||
					form.labels.formType === FormTypes.FINAL_REPORT,
			)
			.sort((a) => (a.labels.formType === FormTypes.FINAL_REPORT ? 1 : -1));
		const supplementary = sortedForms.filter(
			(form) => form.labels.formType === FormTypes.SUPPLEMENTARY,
		);
		const reference = sortedForms.filter(
			(form) => form.labels.formType === FormTypes.REFERENCE,
		);

		return [...coversheets, ...main, ...supplementary, ...reference];
	}

	async generateFormInstances(
		formTemplates: FormTemplateWithVersion[],
		tags: CustomTags,
	): Promise<FormInstanceVM[]> {
		const instances: FormInstanceVM[] = [];

		for await (const formTemplate of formTemplates) {
			// This evaluates the context on the form using the tags to generate fields
			const formInstances = await this.globalFormsPdfService.evalExpression(
				'context',
				formTemplate?.context,
				tags,
				formTemplate,
				null,
			);

			const _instances = formInstances?.map((formInstance) => {
				// Provide a list of field mappings, sorted by priority to ensure that the fields are evaluated in the correct order
				// This ensures that field dependencies are evaluated before the fields that depend on them (e.g. barcodes).
				const fieldMappings = Object.entries(
					formTemplate.version.fieldMappings || {},
				)
					.map(([key, fieldMapping]) => {
						return {
							...fieldMapping,
							priority: fieldMapping.priority ?? 1,
							fieldId: key,
						};
					})
					.sort((a, b) => a.priority - b.priority);

				//const fields = await this.evaluateFields(tags, fieldMappings, formInstance);
				const form: FormInstanceVM = {
					id: formTemplate.id,
					storageRefPath: formTemplate.version.storageRefPath,
					barcodeStorageRefPath: formTemplate.version.barcodeStorageRefPath,
					context: formInstance,
					//fields,
					fields: {},
					formFieldMappings: fieldMappings,
					//version: formTemplate.version,
					name: formInstance.name || formTemplate.name,
					selected: false,
					labels: formTemplate.labels,
				};

				// TODO: Fix newFlagExpiryDate handling SC-23167
				// if (formTemplate.labels?.newFlagExpiryDate) {
				// 	form.newFlagExpiryDate =
				// 		formTemplate.labels.newFlagExpiryDate.toDate();
				// }

				if (formTemplate.labels?.doesExpiryDateApplyToSync) {
					form.doesExpiryDateApplyToSync =
						formTemplate.labels.doesExpiryDateApplyToSync;
				}

				return form;
			});

			if (_instances) {
				instances.push(..._instances);
			}
		}

		return instances;
	}

	private async isVoidChequeRequired(): Promise<boolean> {
		const mortgage = this._mortgage;

		if (!mortgage || !this.selectedChargeType) {
			return false;
		}

		const primaryLender = getMortgagePrimaryLender(mortgage);
		if (!primaryLender?.closerSyncId) {
			return false;
		}

		try {
			const institutions = await lastValueFrom(
				this.syncInstitutions$.pipe(take(1)),
			);
			const institution = institutions?.find(
				(inst) => inst.closerSyncId === primaryLender.closerSyncId,
			);

			return (
				institution?.chargeTypes?.[this.selectedChargeType]?.voidCheque === true
			);
		} catch {
			return false;
		}
	}

	private async autoIncludeVoidChequeAfterRFF(
		selectedForms: LenderDocument[],
		selectedUploadedDoc: LenderDocument[],
	): Promise<void> {
		const rffIndex = selectedForms.findIndex(
			(doc) => doc.labels?.formType === FormTypes.REQUEST_FOR_FUNDS,
		);

		if (rffIndex === -1) {
			return;
		}

		const voidChequeRequired = await this.isVoidChequeRequired();
		const voidChequeAlreadySelected = this.isVoidChequeAlreadySelected(
			selectedForms,
			selectedUploadedDoc,
		);

		if (voidChequeRequired && !voidChequeAlreadySelected) {
			const voidChequeDoc = this.VM.uploadedDocuments.find(
				(doc) => doc.id === 'voidCheque',
			);

			if (voidChequeDoc) {
				const voidChequeLenderDoc: LenderDocument = {
					id: null,
					name: voidChequeDoc.name,
					path: voidChequeDoc.url,
					fields: null,
				};
				selectedForms.splice(rffIndex + 1, 0, voidChequeLenderDoc);
				Logger.info('Auto-included void cheque after RFF');
			} else {
				Logger.warn('Void cheque required but not found in trust account');
			}
		}
	}

	private isVoidChequeAlreadySelected(
		selectedForms: LenderDocument[],
		selectedUploadedDoc: LenderDocument[],
	): boolean {
		const isVoidCheque = (doc: LenderDocument) =>
			doc.name === 'Void Cheque' || doc.path?.includes('voidCheque');

		return (
			selectedForms.some(isVoidCheque) || selectedUploadedDoc.some(isVoidCheque)
		);
	}

	async mergedDocuments({
		isDownload = false,
		isAutoInclude = false,
	} = {}): Promise<LenderDocument[]> {
		const selectedUploadedDoc = this.VM.uploadedDocuments
			.filter((doc) => doc.selected)
			.map((doc) => ({
				id: null,
				name: doc.name,
				path: doc.url,
				fields: null,
			}));

		const _selectedForms = this.VM.forms.filter((form) => form.selected);

		if (needsCoversheetForm(_selectedForms, isDownload)) {
			const forms = this.VM.forms;
			const coverSheet = forms.filter(
				(doc) =>
					doc.labels.formType === FormTypes['COVER_SHEET'] ||
					doc.name.toLocaleLowerCase().includes('cover'),
			);

			if (coverSheet.length > 0) {
				_selectedForms.unshift(coverSheet[0]);
			}
		}

		const selectedForms: LenderDocument[] = [];

		for await (const form of _selectedForms) {
			const overriddenFormData = this.formData.find(
				(data) => data.id === encodeURIComponent(form.context.id),
			);

			const tagsFields = await this.globalFormsPdfService.evaluateFields(
				form.formFieldMappings,
				form.context,
			);

			const fields = {};

			Object.entries(tagsFields).forEach(([key, value]) => {
				fields[key] = value.tagValue;
			});

			if (overriddenFormData && overriddenFormData.fields) {
				Object.entries(overriddenFormData.fields).forEach(([key, value]) => {
					if (isSet(value.override)) {
						fields[base64ToString(key)] = value.override;
					}
				});
			}

			const lenderDoc: LenderDocument = {
				id: form.id,
				name: form.name,
				path: form.storageRefPath,
				fields: fields,
				labels: form.labels,
			};

			selectedForms.push(lenderDoc);
		}

		if (isAutoInclude && !isDownload) {
			await this.autoIncludeVoidChequeAfterRFF(
				selectedForms,
				selectedUploadedDoc,
			);
		}

		return [...selectedForms, ...selectedUploadedDoc];
	}

	async sendToLender() {
		const mergedDocuments = await this.mergedDocuments({ isAutoInclude: true });

		let requiresLawyerSignature = false;

		let mortgageStep: MortgageStepType;

		if (
			mergedDocuments.some((doc) =>
				doc.labels?.formType?.includes('requestForFunds'),
			)
		) {
			mortgageStep = MortgageStepType.REQUEST_FOR_FUNDS;
			requiresLawyerSignature = true;
		} else if (
			mergedDocuments.some((doc) =>
				doc.labels?.formType?.includes('finalReport'),
			)
		) {
			requiresLawyerSignature = true;
			mortgageStep = MortgageStepType.FINAL_REPORT;
		} else if (isSet(this.matterClosingDate)) {
			const closingDate = parse(
				this.matterClosingDate,
				'yyyy-MM-dd',
				new Date(),
			);
			const tomorrow = startOfDay(new Date());
			mortgageStep = MortgageStepType.REQUEST_FOR_FUNDS;
			if (isBefore(closingDate, tomorrow)) {
				mortgageStep = MortgageStepType.FINAL_REPORT;
			}
		} else {
			mortgageStep = MortgageStepType.REQUEST_FOR_FUNDS;
		}

		requiresLawyerSignature =
			requiresLawyerSignature ||
			mergedDocuments.some((doc) =>
				doc.labels?.formType?.includes('reporting'),
			);

		const primaryLender = getMortgagePrimaryLender(this._mortgage);

		const data: SendPackageToLenderComponentDialogData = {
			mortgageId: this._mortgage.id,
			lenderName: primaryLender?.shortName || primaryLender?.name || '',
			instructionId: this._mortgage.relatedMortgageId,
			lawyerId: this.lawyer.id,
			mortgageStep,
			requiresLawyerSignature,
			documents: mergedDocuments,
			syncFlowType:
				this._mortgage.syncFlowType ?? this.syncFlowType ?? SYNC_FLOW_TYPES.FAX,
		};

		const dialogRef = this.dialog.open(SendPackageToLenderComponent, {
			data,
			panelClass: 'no-padding',
		});

		dialogRef.componentInstance.sendToLenderStateChanged
			.pipe(untilDestroyed(this))
			.subscribe((result) => {
				if (result === 'sent') {
					this.gotoHistory.emit();
				}
			});
	}

	async downloadFiles() {
		this.VM = { downloading: true };

		try {
			this.analytics.logEvent(AnalyticsEvent.SYNC_MORTGAGE_PAGE_DOWNLOAD);
			const _mergedDocuments = await this.mergedDocuments({ isDownload: true });

			const documents: PreparePackageRequest[] = _mergedDocuments.map(
				(document) => ({
					formId: document.id,
					path: document.path,
					fieldOverrides: document.fields,
				}),
			);
			const ignoreSignature = true;

			const fileBase64 = await lastValueFrom(
				this.closerSyncInstructionsAPIService.preparePackage(
					this.lawyer?.id,
					documents,
					'Unable to download file',
					ignoreSignature,
				),
			);

			const blob = base64ToBlob(fileBase64, 'application/pdf');

			const fileDownload: FileDownload = {
				name:
					_mergedDocuments?.length > 1
						? 'Mortgage Documents'
						: _mergedDocuments[0]?.name,
				type: 'pdf',
				data: blob,
				dataType: FileDownloadType.BLOB,
			};

			this.fileDownloadDispatch.DownloadFile(fileDownload);
		} finally {
			this.VM = { downloading: false };
		}
	}

	isUndeletableUserUploadedDoc(docId: string): boolean {
		return !this.VM.undeletableUserUploadedDocIds.includes(docId);
	}

	deleteUploadedDocument(docId: string) {
		this.matterUploadedFilesDispatch.Delete(docId);
	}

	public connectToSync() {
		this.navService.goToProfile_Integrations_CloserSync_ConnectModal(
			this.lawyer.id,
		);
	}
}
